import { useEffect, useState } from "react";
import { localDateStr, type ChargeBase } from "@tripcart/shared";

import { fetchUnitsLeft, unitsLeftById } from "@/lib/units-left";

/**
 * The most of each Assets / Rentals category one booking can take on the
 * picked date — the resource's units that day (a capacity change included),
 * less what is booked, within the tour's own limit. Empty until a date and a
 * package are picked, and for categories that draw from no resource.
 *
 * A timed tour's slot is picked later, so each of the date's start times is
 * asked and the best one wins: a jeep taken at 09:00 may still be free at
 * 14:00. Checkout refuses a slot that turns out full (`UNITS_EXHAUSTED`).
 */
export function useUnitCaps(args: {
  experienceId: string | undefined;
  packageId: string | undefined;
  date: Date | undefined;
  /** The picked date's start times, 24-hour `HH:MM`; empty for a date-only
   *  tour. */
  startTimes: readonly string[];
  categories: ReadonlyArray<{ id: string; chargeBase: ChargeBase }>;
}): Record<string, number> {
  const { experienceId, packageId, date } = args;
  const dateIso = date ? localDateStr(date) : null;
  const assetIds = args.categories
    .filter((c) => c.chargeBase === "asset")
    .map((c) => c.id)
    .join(",");
  const key =
    experienceId && packageId && dateIso && assetIds
      ? `${experienceId}|${packageId}|${dateIso}|${assetIds}|${args.startTimes.join(",")}`
      : null;
  const [caps, setCaps] = useState<{
    key: string;
    byId: Record<string, number>;
  } | null>(null);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const [experience, pkg, day, ids, times] = key.split("|");
    const slots = times ? times.split(",") : [null];
    void Promise.all(
      slots.map((startTime) =>
        fetchUnitsLeft({
          experienceId: experience!,
          packageId: pkg!,
          date: day!,
          startTime,
          priceCategoryIds: ids!.split(","),
        }),
      ),
    ).then((answers) => {
      if (!cancelled)
        setCaps({ key, byId: bestSlot(answers.map(unitsLeftById)) });
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return caps && caps.key === key ? caps.byId : NO_CAPS;
}

/** Per category, the most any one slot has left. A slot whose answer did not
 *  arrive knows no limit, so it leaves the category uncapped. */
function bestSlot(
  slots: readonly Record<string, number>[],
): Record<string, number> {
  if (slots.some((s) => Object.keys(s).length === 0)) return NO_CAPS;
  const out: Record<string, number> = {};
  for (const slot of slots) {
    for (const [id, left] of Object.entries(slot)) {
      out[id] = Math.max(out[id] ?? 0, left);
    }
  }
  return out;
}

const NO_CAPS: Record<string, number> = {};
